/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * Segnalazioni del tecnico (migrazioni 015 e 016).
 *
 * Il tecnico usa l'app sul campo, dal telefono, spesso con le mani occupate: qui
 * dice cosa non va o cosa gli servirebbe, e il modo piu' rapido e' a voce. Vede
 * solo le proprie, segue lo stato e risponde all'ufficio; quando lo stato cambia
 * gli arriva un avviso.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Bug, Sparkles, Lightbulb, Mic, Image as ImageIcon, Trash2, Send, ChevronRight } from 'lucide-react';
import TecnicoLayout from '../components/TecnicoLayout';
import RegistratoreAudio from '../components/RegistratoreAudio';
import { segnalazioniAPI } from '../api/segnalazioni';

const ACCENT = '#10B981';

const TIPI = {
  bug:           { label: 'Non funziona',  icon: Bug,       color: '#B91C1C', bg: '#FEE2E2', spiega: 'Qualcosa nell\'app non va' },
  miglioramento: { label: 'Si può fare meglio', icon: Sparkles, color: '#1D4ED8', bg: '#DBEAFE', spiega: 'Funziona, ma è scomodo' },
  funzionalita:  { label: 'Mi servirebbe', icon: Lightbulb, color: '#047857', bg: '#D1FAE5', spiega: 'Una cosa che oggi non c\'è' },
};

const STATI = {
  nuova:           { label: 'Inviata',         color: '#374151', bg: '#F3F4F6' },
  presa_in_carico: { label: 'Presa in carico', color: '#6D28D9', bg: '#EDE9FE' },
  in_corso:        { label: 'In lavorazione',  color: '#B45309', bg: '#FEF3C7' },
  completata:      { label: 'Fatta',           color: '#065F46', bg: '#D1FAE5' },
  rifiutata:       { label: 'Non prevista',    color: '#991B1B', bg: '#FEE2E2' },
};

const errMsg = (e, def) => e?.response?.data?.error || def;

function fmtData(d) {
  return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function Segnalazioni() {
  const [nuova, setNuova] = useState(false);
  const [aperta, setAperta] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['segnalazioni'],
    queryFn: () => segnalazioniAPI.list({ stato: 'tutte' }),
  });

  return (
    <TecnicoLayout title="Segnala">
      <div style={s.intro}>
        Qualcosa nell'app non va, o ti servirebbe una cosa che non c'è? Scrivilo o
        dillo a voce: l'ufficio lo legge e ti aggiorna qui.
      </div>
      <button style={s.btnGrande} onClick={() => setNuova(true)}>
        <Plus size={20} /> Nuova segnalazione
      </button>

      <h3 style={s.titoloSezione}>Le tue segnalazioni</h3>
      {isLoading ? (
        <div style={s.vuoto}>Caricamento...</div>
      ) : error ? (
        <div style={s.vuoto}>{errMsg(error, 'Errore nel caricamento')}</div>
      ) : data.segnalazioni.length === 0 ? (
        <div style={s.vuoto}>Non hai ancora inviato segnalazioni.</div>
      ) : (
        <div style={s.elenco}>
          {data.segnalazioni.map((x) => {
            const t = TIPI[x.tipo];
            const st = STATI[x.stato];
            const Icona = t.icon;
            return (
              <button key={x.id} style={s.riga} onClick={() => setAperta(x.id)}>
                <span style={{ ...s.iconaTipo, backgroundColor: t.bg, color: t.color }}><Icona size={18} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={s.rigaTitolo}>{x.titolo}</span>
                  <span style={{ ...s.badge, backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                  {(x.n_audio > 0 || x.n_immagini > 0) && (
                    <span style={s.allegatiMini}>
                      {x.n_audio > 0 && <><Mic size={12} /> {x.n_audio} </>}
                      {x.n_immagini > 0 && <><ImageIcon size={12} /> {x.n_immagini}</>}
                    </span>
                  )}
                </span>
                <ChevronRight size={18} color="#9CA3AF" />
              </button>
            );
          })}
        </div>
      )}

      {nuova && <Nuova onChiudi={() => setNuova(false)} onCreata={(id) => { setNuova(false); setAperta(id); }} />}
      {aperta && <Dettaglio id={aperta} onChiudi={() => setAperta(null)} />}
    </TecnicoLayout>
  );
}

function Nuova({ onChiudi, onCreata }) {
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState('bug');
  const [titolo, setTitolo] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [allegati, setAllegati] = useState([]);
  const [errore, setErrore] = useState('');
  const [invio, setInvio] = useState(false);

  useEffect(() => () => allegati.forEach((a) => URL.revokeObjectURL(a.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const aggiungi = (file, nome, secondi, tipoAll) =>
    setAllegati((a) => [...a, { file, nome, secondi, tipo: tipoAll, url: URL.createObjectURL(file) }].slice(0, 10));
  const togli = (i) => setAllegati((a) => { URL.revokeObjectURL(a[i].url); return a.filter((_, k) => k !== i); });

  // Il titolo si puo' saltare se c'e' un audio: dal furgone, con le mani sporche,
  // e' gia' molto aver registrato.
  const conAudio = allegati.some((a) => a.tipo === 'audio');
  const invia = async () => {
    const t = titolo.trim() || (conAudio ? `Messaggio vocale: ${TIPI[tipo].label.toLowerCase()}` : '');
    if (!t) { setErrore('Scrivi due parole, oppure registra un messaggio vocale'); return; }
    setInvio(true);
    setErrore('');
    try {
      const creata = await segnalazioniAPI.crea({ tipo, titolo: t, descrizione });
      const falliti = [];
      for (const a of allegati) {
        try { await segnalazioniAPI.caricaAllegato(creata.id, a.file, a.nome, a.secondi); } catch (e) { falliti.push(a.nome); }
      }
      queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
      if (falliti.length) window.alert(`Segnalazione inviata, ma non sono riuscito a caricare: ${falliti.join(', ')}. Riprova dal dettaglio.`);
      onCreata(creata.id);
    } catch (e) {
      setErrore(errMsg(e, 'Invio non riuscito'));
      setInvio(false);
    }
  };

  return (
    <div style={s.foglio}>
      <div style={s.foglioTesta}>
        <h3 style={s.foglioTitolo}>Nuova segnalazione</h3>
        <button style={s.iconaBtn} onClick={onChiudi} aria-label="Chiudi"><X size={22} /></button>
      </div>
      <div style={s.foglioCorpo}>
        <div style={s.tipi}>
          {Object.entries(TIPI).map(([k, t]) => {
            const Icona = t.icon;
            return (
              <button key={k} type="button" onClick={() => setTipo(k)}
                style={{ ...s.tipoBtn, ...(tipo === k ? { borderColor: t.color, backgroundColor: t.bg } : {}) }}>
                <Icona size={20} color={t.color} />
                <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.label}</span>
                <span style={{ fontSize: '0.76rem', color: '#6B7280' }}>{t.spiega}</span>
              </button>
            );
          })}
        </div>

        <label style={s.label}>Dillo a voce</label>
        <RegistratoreAudio disabilitato={invio || allegati.length >= 10}
          onPronto={(blob, nome, secondi) => aggiungi(blob, nome, secondi, 'audio')} />

        <label style={s.label}>In due parole</label>
        <input style={s.input} value={titolo} maxLength={200} onChange={(e) => setTitolo(e.target.value)}
          placeholder={tipo === 'bug' ? 'es. Il timer non parte' : 'es. Vedere lo storico dell\'impianto'} />

        <label style={s.label}>Dettagli (se vuoi)</label>
        <textarea style={{ ...s.input, minHeight: '90px' }} value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
          placeholder={tipo === 'bug' ? 'Cosa stavi facendo e cosa è successo' : 'A cosa ti servirebbe'} />

        <label style={s.btnFoto}>
          <ImageIcon size={18} /> Foto o schermata
          <input type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={(e) => { Array.from(e.target.files || []).forEach((f) => aggiungi(f, f.name, null, 'immagine')); e.target.value = ''; }} />
        </label>

        {allegati.length > 0 && (
          <div style={s.allegati}>
            {allegati.map((a, i) => (
              <div key={i} style={s.allegato}>
                {a.tipo === 'audio'
                  ? <audio controls src={a.url} style={{ height: '36px', flex: 1, minWidth: 0 }} />
                  : <img src={a.url} alt="" style={s.miniatura} />}
                <button style={s.iconaBtn} onClick={() => togli(i)} aria-label="Togli"><Trash2 size={17} /></button>
              </div>
            ))}
          </div>
        )}

        {errore && <div style={s.errore}>{errore}</div>}
      </div>
      <div style={s.foglioPiede}>
        <button style={s.btnGrande} onClick={invia} disabled={invio}>
          <Send size={18} /> {invio ? 'Invio...' : 'Invia'}
        </button>
      </div>
    </div>
  );
}

function AllegatoMedia({ allegato }) {
  const [url, setUrl] = useState(null);
  const [errore, setErrore] = useState(false);
  useEffect(() => {
    let vivo = true;
    let creato = null;
    segnalazioniAPI.scaricaAllegato(allegato.id)
      .then((blob) => { if (!vivo) return; creato = URL.createObjectURL(blob); setUrl(creato); })
      .catch(() => vivo && setErrore(true));
    return () => { vivo = false; if (creato) URL.revokeObjectURL(creato); };
  }, [allegato.id]);
  if (errore) return <span style={s.piccolo}>Allegato non disponibile</span>;
  if (!url) return <span style={s.piccolo}>Caricamento...</span>;
  if (allegato.tipo === 'audio') return <audio controls src={url} style={{ height: '36px', width: '100%' }} />;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" style={s.immagine} /></a>;
}

function Dettaglio({ id, onChiudi }) {
  const queryClient = useQueryClient();
  const chiave = ['segnalazione', id];
  const [commento, setCommento] = useState('');
  const [errore, setErrore] = useState('');
  const { data: x, isLoading, error } = useQuery({ queryKey: chiave, queryFn: () => segnalazioniAPI.detail(id) });

  const aggiorna = () => {
    setErrore('');
    queryClient.invalidateQueries({ queryKey: chiave });
    queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
  };
  const fallito = (e) => setErrore(errMsg(e, 'Operazione non riuscita'));
  const commenta = useMutation({
    mutationFn: () => segnalazioniAPI.commenta(id, commento),
    onSuccess: () => { setCommento(''); aggiorna(); }, onError: fallito,
  });
  const allega = useMutation({
    mutationFn: ({ file, nome, secondi }) => segnalazioniAPI.caricaAllegato(id, file, nome, secondi),
    onSuccess: aggiorna, onError: fallito,
  });
  const elimina = useMutation({
    mutationFn: () => segnalazioniAPI.elimina(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['segnalazioni'] }); onChiudi(); },
    onError: fallito,
  });

  const chiusa = x && ['completata', 'rifiutata'].includes(x.stato);

  return (
    <div style={s.foglio}>
      <div style={s.foglioTesta}>
        <h3 style={s.foglioTitolo}>{x ? x.titolo : 'Segnalazione'}</h3>
        <button style={s.iconaBtn} onClick={onChiudi} aria-label="Chiudi"><X size={22} /></button>
      </div>
      <div style={s.foglioCorpo}>
        {isLoading ? <div style={s.vuoto}>Caricamento...</div> : error ? <div style={s.vuoto}>{errMsg(error, 'Errore')}</div> : (
          <>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              <span style={{ ...s.badge, backgroundColor: TIPI[x.tipo].bg, color: TIPI[x.tipo].color }}>{TIPI[x.tipo].label}</span>
              <span style={{ ...s.badge, backgroundColor: STATI[x.stato].bg, color: STATI[x.stato].color }}>{STATI[x.stato].label}</span>
            </div>
            <div style={{ ...s.piccolo, marginTop: '0.3rem' }}>Inviata il {fmtData(x.created_at)}</div>

            {x.risposta && (
              <div style={{ ...s.risposta, borderColor: STATI[x.stato].color }}>
                <strong>{x.stato === 'rifiutata' ? 'Perché non è prevista' : 'Risposta dell\'ufficio'}:</strong> {x.risposta}
              </div>
            )}
            {x.descrizione && <p style={s.descrizione}>{x.descrizione}</p>}

            {x.allegati.length > 0 && (
              <div style={s.allegati}>
                {x.allegati.map((a) => <div key={a.id} style={s.allegato}><div style={{ flex: 1, minWidth: 0 }}><AllegatoMedia allegato={a} /></div></div>)}
              </div>
            )}
            {!chiusa && x.allegati.length < 10 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                <RegistratoreAudio disabilitato={allega.isPending} onPronto={(file, nome, secondi) => allega.mutate({ file, nome, secondi })} />
                <label style={s.btnFoto}>
                  <ImageIcon size={18} /> Foto
                  <input type="file" accept="image/*" style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) allega.mutate({ file: f, nome: f.name }); e.target.value = ''; }} />
                </label>
              </div>
            )}

            <h4 style={s.titoloSezione}>Conversazione</h4>
            <div style={s.storia}>
              {x.storico.map((h) => (
                <div key={h.id} style={{ ...s.messaggio, ...(h.mio ? s.messaggioMio : {}) }}>
                  <div style={s.piccolo}>{h.mio ? 'Tu' : h.utente} · {fmtData(h.created_at)}</div>
                  {h.stato_nuovo && (
                    <div style={{ fontSize: '0.85rem' }}>
                      {h.stato_precedente ? <>Stato: <strong>{STATI[h.stato_nuovo]?.label}</strong></> : 'Inviata'}
                    </div>
                  )}
                  {h.commento && <div style={s.commento}>{h.commento}</div>}
                </div>
              ))}
            </div>

            {errore && <div style={s.errore}>{errore}</div>}

            {x.stato === 'nuova' && (
              <button style={s.btnElimina} onClick={() => window.confirm('Ritirare la segnalazione?') && elimina.mutate()}>
                <Trash2 size={15} /> Ritira la segnalazione
              </button>
            )}
          </>
        )}
      </div>
      {x && (
        <div style={{ ...s.foglioPiede, display: 'flex', gap: '0.5rem' }}>
          <input style={{ ...s.input, flex: 1 }} value={commento} onChange={(e) => setCommento(e.target.value)} placeholder="Scrivi all'ufficio..." />
          <button style={{ ...s.btnInvia, opacity: commento.trim() ? 1 : 0.5 }} disabled={!commento.trim() || commenta.isPending}
            onClick={() => commenta.mutate()} aria-label="Invia">
            <Send size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  intro: { fontSize: '0.9rem', color: '#4B5563', lineHeight: 1.5, marginBottom: '0.9rem' },
  btnGrande: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.9rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', minHeight: '52px' },
  titoloSezione: { fontSize: '0.95rem', fontWeight: 700, margin: '1.4rem 0 0.6rem', color: '#111827' },
  elenco: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  riga: { display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%', padding: '0.85rem', backgroundColor: 'white', border: 'none', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' },
  iconaTipo: { width: '40px', height: '40px', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  rigaTitolo: { display: 'block', fontWeight: 600, fontSize: '0.95rem', color: '#111827', marginBottom: '0.3rem' },
  badge: { display: 'inline-block', padding: '0.18rem 0.55rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 },
  allegatiMini: { display: 'inline-flex', alignItems: 'center', gap: '0.2rem', marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6B7280' },
  vuoto: { padding: '1.5rem', textAlign: 'center', color: '#6B7280', backgroundColor: 'white', borderRadius: '12px' },
  foglio: { position: 'fixed', inset: 0, backgroundColor: '#F9FAFB', zIndex: 1000, display: 'flex', flexDirection: 'column' },
  foglioTesta: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.9rem 1rem', backgroundColor: 'white', borderBottom: '1px solid #E5E7EB' },
  foglioTitolo: { margin: 0, fontSize: '1.05rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  foglioCorpo: { flex: 1, overflowY: 'auto', padding: '1rem' },
  foglioPiede: { padding: '0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom))', backgroundColor: 'white', borderTop: '1px solid #E5E7EB' },
  tipi: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  tipoBtn: { display: 'grid', gridTemplateColumns: '28px 1fr', gridTemplateRows: 'auto auto', columnGap: '0.6rem', alignItems: 'center', padding: '0.75rem', border: '2px solid #E5E7EB', borderRadius: '12px', background: 'white', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', margin: '1rem 0 0.35rem' },
  input: { width: '100%', padding: '0.75rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '1rem', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white' },
  btnFoto: { display: 'inline-flex', alignItems: 'center', gap: '0.45rem', marginTop: '0.9rem', padding: '0.6rem 0.95rem', backgroundColor: '#F3F4F6', color: '#374151', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px', boxSizing: 'border-box' },
  allegati: { display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem' },
  allegato: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem', backgroundColor: 'white', borderRadius: '10px' },
  miniatura: { width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px' },
  immagine: { maxWidth: '100%', maxHeight: '280px', borderRadius: '8px' },
  iconaBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'inline-flex', padding: '0.4rem' },
  errore: { marginTop: '0.8rem', padding: '0.65rem 0.8rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.88rem' },
  piccolo: { fontSize: '0.78rem', color: '#6B7280' },
  risposta: { marginTop: '0.8rem', padding: '0.7rem 0.85rem', borderLeft: '4px solid', backgroundColor: 'white', borderRadius: '6px', fontSize: '0.9rem', lineHeight: 1.5 },
  descrizione: { fontSize: '0.95rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: '0.9rem 0 0' },
  storia: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  messaggio: { backgroundColor: 'white', borderRadius: '12px', padding: '0.6rem 0.8rem', maxWidth: '88%' },
  messaggioMio: { alignSelf: 'flex-end', backgroundColor: '#D1FAE5' },
  commento: { fontSize: '0.92rem', whiteSpace: 'pre-wrap', marginTop: '0.2rem' },
  btnElimina: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginTop: '1.25rem', padding: '0.55rem 0.9rem', backgroundColor: 'white', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: '10px', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' },
  btnInvia: { width: '48px', height: '48px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer' },
};
